import { useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, Circle, FileSpreadsheet, MinusCircle, Plus } from "lucide-react";
import { TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import { useAuth } from "@/lib/auth/auth-provider";
import { useRole } from "@/lib/auth/use-role";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Dot,
  PageHeader,
  Spinner,
} from "@/components/ui/primitives";
import {
  addChecklistItem,
  getRequirement,
  getTask,
  listChecklistItems,
  listTaskEvents,
  setChecklistItemStatus,
  setTaskStatus,
  type ChecklistItemRow,
  type ChecklistStatus,
  type TaskRow,
  type TaskStatus,
} from "@/lib/api/compliance";
import { createFormPfFiling } from "@/lib/api/filings";
import {
  AUTHORITY_LABEL,
  CATEGORY_LABEL,
  STATUS_LABEL,
  STATUS_TONE,
  TASK_STATUS_FLOW,
  URGENCY_LABEL,
  URGENCY_TONE,
} from "@/lib/compliance-labels";
import { checklistProgress, taskUrgency } from "@argus/engine";

const TODAY = new Date().toISOString().slice(0, 10);

function TaskInner({ taskId }: { taskId: string }) {
  const tenant = useTenant();
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { canWrite, role } = useRole(tenant.id);
  const canEdit = canWrite || role === "reviewer";
  const [completeNote, setCompleteNote] = useState("");
  const [newItem, setNewItem] = useState("");

  const {
    data: task,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["compliance-task", taskId],
    queryFn: () => getTask(taskId),
  });
  const requirementId = task?.requirement_id;
  const { data: requirement } = useQuery({
    queryKey: ["compliance-requirement", requirementId],
    queryFn: () => getRequirement(requirementId ?? ""),
    enabled: !!requirementId,
  });
  const { data: items } = useQuery({
    queryKey: ["compliance-checklist", taskId],
    queryFn: () => listChecklistItems(taskId),
  });
  const { data: events } = useQuery({
    queryKey: ["compliance-task-events", taskId],
    queryFn: () => listTaskEvents(taskId),
  });

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ["compliance-task", taskId] });
    void qc.invalidateQueries({ queryKey: ["compliance-checklist", taskId] });
    void qc.invalidateQueries({ queryKey: ["compliance-task-events", taskId] });
    void qc.invalidateQueries({ queryKey: ["compliance-calendar", tenant.id] });
  }

  const transition = useMutation({
    mutationFn: ({ t, to, note }: { t: TaskRow; to: TaskStatus; note?: string }) => {
      if (!user) throw new Error("Not signed in.");
      return setTaskStatus(t, to, user.id, note);
    },
    onSuccess: () => {
      setCompleteNote("");
      invalidate();
    },
  });

  const toggleItem = useMutation({
    mutationFn: ({ item, status }: { item: ChecklistItemRow; status: ChecklistStatus }) => {
      if (!user) throw new Error("Not signed in.");
      return setChecklistItemStatus(item, status, user.id);
    },
    onSuccess: invalidate,
  });

  const addItem = useMutation({
    mutationFn: () => addChecklistItem(tenant.id, taskId, newItem.trim(), items?.length ?? 0),
    onSuccess: () => {
      setNewItem("");
      invalidate();
    },
  });

  // Form PF deadlines hand off to the filing workspace (Phase 3).
  const openFiling = useMutation({
    mutationFn: () => {
      if (!user || !task) throw new Error("Not ready.");
      return createFormPfFiling({
        tenantId: tenant.id,
        periodLabel: task.period_label ?? task.title,
        dueDate: task.due_date,
        complianceTaskId: task.id,
        userId: user.id,
      });
    },
    onSuccess: (filing) =>
      void navigate({
        to: "/t/$slug/filings/$filingId",
        params: { slug: tenant.slug, filingId: filing.id },
      }),
  });

  if (isLoading) return <p className="text-sm text-neutral-500">Loading task…</p>;
  if (error || !task)
    return (
      <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
        {error?.message ?? "Task not found."}
      </div>
    );

  const urgency = taskUrgency(task.due_date, task.lead_time_days, task.status, TODAY);
  const progress = checklistProgress(items ?? []);

  return (
    <>
      <Link
        to="/t/$slug/calendar"
        params={{ slug: tenant.slug }}
        className="inline-flex items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-300"
      >
        <ArrowLeft className="size-3.5" /> Calendar
      </Link>

      <PageHeader
        title={task.title}
        subtitle={
          requirement ? (
            <span className="flex flex-wrap items-center gap-1.5">
              <Badge tone="neutral">{AUTHORITY_LABEL[requirement.authority]}</Badge>
              <span>{CATEGORY_LABEL[requirement.category]}</span>
              {requirement.citation && (
                <span className="text-neutral-600">· {requirement.citation}</span>
              )}
              {task.period_label && <span className="text-neutral-600">· {task.period_label}</span>}
            </span>
          ) : undefined
        }
        actions={
          <div className="flex items-center gap-2">
            <Badge tone={URGENCY_TONE[urgency]}>
              <Dot tone={URGENCY_TONE[urgency]} /> {URGENCY_LABEL[urgency]}
            </Badge>
            <Badge tone={STATUS_TONE[task.status]}>{STATUS_LABEL[task.status]}</Badge>
          </div>
        }
      />

      {requirement?.catalog_key === "form_pf" && (
        <Card className="border-brand-900/50 bg-brand-950/20 flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <FileSpreadsheet className="text-brand-400 size-4" />
            <p className="text-sm text-neutral-300">
              Prepare this filing in the workspace — Argus auto-populates fund data, flags gaps, and
              drafts the rest.
            </p>
          </div>
          {canEdit && (
            <Button
              size="sm"
              variant="primary"
              disabled={openFiling.isPending}
              onClick={() => openFiling.mutate()}
            >
              {openFiling.isPending ? (
                <Spinner className="size-3.5" />
              ) : (
                <FileSpreadsheet className="size-3.5" />
              )}
              Open Form PF workspace
            </Button>
          )}
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        {/* Checklist */}
        <Card>
          <CardHeader
            title={`Checklist — ${progress.collected}/${progress.applicable} collected`}
            action={
              <div className="flex items-center gap-2">
                <div className="h-1.5 w-24 overflow-hidden rounded-full bg-neutral-800">
                  <div
                    className="bg-brand-500 h-full rounded-full transition-all"
                    style={{ width: `${progress.pct}%` }}
                  />
                </div>
                <span className="text-[11px] tabular-nums text-neutral-500">{progress.pct}%</span>
              </div>
            }
          />
          <div className="divide-y divide-neutral-800/70">
            {(items ?? []).length === 0 ? (
              <p className="px-4 py-6 text-center text-xs text-neutral-500">
                No checklist items. {canEdit ? "Add the documents this filing requires below." : ""}
              </p>
            ) : (
              (items ?? []).map((item) => (
                <ChecklistRow
                  key={item.id}
                  item={item}
                  canEdit={canEdit}
                  busy={toggleItem.isPending}
                  onSet={(status) => toggleItem.mutate({ item, status })}
                />
              ))
            )}
            {canEdit && (
              <div className="flex items-center gap-2 px-4 py-2.5">
                <input
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newItem.trim()) addItem.mutate();
                  }}
                  placeholder="Add a checklist item…"
                  className="flex-1 rounded-md border border-neutral-800 bg-neutral-950 px-2.5 py-1.5 text-sm text-neutral-200 placeholder:text-neutral-600"
                />
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={!newItem.trim() || addItem.isPending}
                  onClick={() => addItem.mutate()}
                >
                  <Plus className="size-3.5" /> Add
                </Button>
              </div>
            )}
          </div>
        </Card>

        {/* Status + activity */}
        <div className="space-y-4">
          <Card>
            <CardHeader title="Status" />
            <div className="space-y-3 px-4 pb-4">
              <div className="text-xs text-neutral-400">
                Due <span className="tabular-nums text-neutral-200">{task.due_date}</span> · lead{" "}
                {task.lead_time_days}d
                {task.submitted_at ? (
                  <div className="mt-1 text-neutral-500">
                    Submitted {task.submitted_at.slice(0, 10)}
                  </div>
                ) : null}
              </div>
              {canEdit ? (
                <>
                  <div className="flex flex-wrap gap-1.5">
                    {TASK_STATUS_FLOW.filter((s) => s !== task.status && s !== "completed").map(
                      (s) => (
                        <Button
                          key={s}
                          size="sm"
                          variant="ghost"
                          disabled={transition.isPending}
                          onClick={() => transition.mutate({ t: task, to: s })}
                        >
                          {STATUS_LABEL[s]}
                        </Button>
                      ),
                    )}
                  </div>
                  {task.status !== "completed" && (
                    <div className="space-y-2 rounded-md border border-emerald-900/40 bg-emerald-950/20 p-2.5">
                      <input
                        value={completeNote}
                        onChange={(e) => setCompleteNote(e.target.value)}
                        placeholder="Completion note (optional)…"
                        className="w-full rounded border border-neutral-800 bg-neutral-950 px-2 py-1 text-xs text-neutral-200 placeholder:text-neutral-600"
                      />
                      <Button
                        size="sm"
                        variant="success"
                        className="w-full"
                        disabled={!progress.complete || transition.isPending}
                        title={progress.complete ? "" : "Collect all checklist items first"}
                        onClick={() =>
                          transition.mutate({ t: task, to: "completed", note: completeNote })
                        }
                      >
                        {transition.isPending ? (
                          <Spinner className="size-3.5" />
                        ) : (
                          <Check className="size-3.5" />
                        )}
                        Mark complete
                      </Button>
                      {!progress.complete && (
                        <p className="text-[11px] text-amber-300/80">
                          {progress.outstanding} checklist item
                          {progress.outstanding === 1 ? "" : "s"} outstanding
                        </p>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-xs text-neutral-500">You have read-only access to this task.</p>
              )}
              {transition.error && (
                <p className="text-[11px] text-red-300">{transition.error.message}</p>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title="Activity" />
            <div className="max-h-72 space-y-2.5 overflow-y-auto px-4 pb-4 text-xs">
              {(events ?? []).length === 0 ? (
                <p className="text-neutral-500">No activity yet.</p>
              ) : (
                (events ?? []).map((ev) => (
                  <div key={ev.id} className="flex gap-2">
                    <Dot tone="neutral" className="mt-1.5" />
                    <div className="min-w-0">
                      <div className="text-neutral-300">
                        {ev.event_type === "status_change" || ev.event_type === "reopened"
                          ? `${ev.from_status ?? "?"} → ${ev.to_status ?? "?"}`
                          : ev.event_type.replace(/_/g, " ")}
                      </div>
                      {ev.note && <div className="text-neutral-500">{ev.note}</div>}
                      <div className="text-neutral-600">
                        {new Date(ev.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

function ChecklistRow({
  item,
  canEdit,
  busy,
  onSet,
}: {
  item: ChecklistItemRow;
  canEdit: boolean;
  busy: boolean;
  onSet: (status: ChecklistStatus) => void;
}) {
  const isCollected = item.status === "collected";
  const isNa = item.status === "not_applicable";
  return (
    <div className="flex items-start gap-3 px-4 py-2.5">
      <button
        disabled={!canEdit || busy}
        onClick={() => onSet(isCollected ? "outstanding" : "collected")}
        className="mt-0.5 shrink-0 disabled:pointer-events-none"
        title={isCollected ? "Mark outstanding" : "Mark collected"}
      >
        {isCollected ? (
          <span className="flex size-4 items-center justify-center rounded-full bg-emerald-600 text-emerald-50">
            <Check className="size-3" />
          </span>
        ) : (
          <Circle className={`size-4 ${isNa ? "text-neutral-700" : "text-neutral-500"}`} />
        )}
      </button>
      <div className="min-w-0 flex-1">
        <div
          className={`text-sm ${isCollected ? "text-neutral-400 line-through" : isNa ? "text-neutral-600 line-through" : "text-neutral-200"}`}
        >
          {item.label}
        </div>
        {item.detail && <div className="text-[11px] text-neutral-500">{item.detail}</div>}
      </div>
      {canEdit && (
        <button
          disabled={busy}
          onClick={() => onSet(isNa ? "outstanding" : "not_applicable")}
          className="mt-0.5 shrink-0 text-neutral-600 hover:text-neutral-400"
          title={isNa ? "Mark applicable" : "Mark N/A"}
        >
          <MinusCircle className="size-3.5" />
        </button>
      )}
    </div>
  );
}

export function ComplianceTaskPage() {
  const { taskId } = useParams({ strict: false });
  return (
    <TenantLayout>
      <TaskInner taskId={taskId ?? ""} />
    </TenantLayout>
  );
}
